// Applications and product reviews as they arrive.
//
// The submissions module announces every application, every product sent for
// review and every decision on `/rt/submissions`
// (aldoo-com-api components/vendor-submissions/broadcast.js). A queue
// subscribes here and refetches when it hears one: the notice carries ids and
// a status, never the records, so what a screen shows is still what its own
// API call is allowed to give it.
//
//   useSubmissionEvents({ onEvent: refresh })          the platform's queues
//   useSubmissionEvents({ orgId, onEvent: refresh })   one vendor's own
//
// Without an orgId the server only admits a reviewer; anyone else is quietly
// not subscribed and the screen behaves as it did before.
import { useEffect, useRef } from "react"
import { useRealtime } from "aldoo-ra/Realtime/hooks"

export const SUBMISSION_EVENTS = ["submissions:application", "submissions:product-review", "submissions:decision"]

// Several screens watch at once — the queue, and the tallies on the tabs —
// and they share one socket. Joining is therefore counted: the room is asked
// for once, and left only when the last of them has gone. Without this the
// first unmount silently unsubscribed the others.
const watchers = new Map()
const key = (socket, orgId) => `${socket.id || "socket"}:${orgId || "all"}`

const join = (socket, orgId) => {
  const id = key(socket, orgId)
  const count = watchers.get(id) || 0
  watchers.set(id, count + 1)
  if (count === 0) socket.emit("submissions:watch", { orgId })
}

const leave = (socket, orgId) => {
  const id = key(socket, orgId)
  const count = watchers.get(id) || 0
  if (count <= 1) {
    watchers.delete(id)
    socket.emit("submissions:unwatch", { orgId })
    return
  }
  watchers.set(id, count - 1)
}

export default function useSubmissionEvents({ orgId = null, events = SUBMISSION_EVENTS, onEvent, enabled = true } = {}) {
  const { socket, connected } = useRealtime(enabled ? "submissions" : null)
  // the handler changes on every render of the caller; the subscription must not
  const handler = useRef(onEvent)
  useEffect(() => {
    handler.current = onEvent
  }, [onEvent])

  useEffect(() => {
    if (!socket || !connected || !enabled) return

    join(socket, orgId)
    const listeners = events.map((event) => {
      const listener = (payload) => handler.current?.({ ...payload, event })
      socket.on(event, listener)
      return () => socket.off(event, listener)
    })

    return () => {
      leave(socket, orgId)
      listeners.forEach((off) => off())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, connected, enabled, orgId, events.join(",")])

  return { connected }
}
